import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	DYNAMIC_UI_CREATE_KEY,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type {
	StreamActionClassification,
	StreamActionPart,
	StreamActionPrepare,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';
import messages from '@atlassian/conversation-assistant/messages/edit-with-rovo-bar';

// eslint-disable-next-line import/extensions -- `.utils` is part of the filename, not a file extension
import { getRemixResultPayload } from '../../state/machine/deriveRemixPhase.utils';
import { useEditorRemixContext } from './context';
import {
	admitAndRetainEditorRemixLifecycle,
	useEditorRemixLifecycleOwner,
} from './editorRemixLifecycleOwner';
import type { RetainedEditorRemixLifecycle } from './editorRemixLifecycleOwner';
import {
	getContentClassificationFromActionClassification,
	getFinalAIFCMessage,
	getMediaIdFromMessage,
	getRemixIdFromMessage,
	withWhiteboardCreateActionData,
} from './utils/message';
import { getFinalMessageId, getMauiIdFromUrl } from './utils/remix-ids';
import { type VariantOption, getInfographicVariants, getVisualAidVariants } from './utils/variant';

const staticVariantsByRemixType: Record<string, VariantOption[]> = {
	chart: [
		{
			value: 'line-chart',
			label: messages.remixVariantLineChart,
			updateChatMessage: messages.chatUpdateLineChart,
		},
		{
			value: 'area-chart',
			label: messages.remixVariantAreaChart,
			updateChatMessage: messages.chatUpdateAreaChart,
		},
		{
			value: 'bar-chart',
			label: messages.remixVariantBarChart,
			updateChatMessage: messages.chatUpdateBarChart,
		},
		{
			value: 'funnel-chart',
			label: messages.remixVariantFunnelChart,
			updateChatMessage: messages.chatUpdateFunnelChart,
		},
		{
			value: 'pie-chart',
			label: messages.remixVariantPieChart,
			updateChatMessage: messages.chatUpdatePieChart,
		},
		{
			value: 'gantt-chart',
			label: messages.remixVariantGanttChart,
			updateChatMessage: messages.chatUpdateGanttChart,
		},
	],
	diagram: [
		{
			value: 'mindmap',
			label: messages.remixVariantMindMap,
			updateChatMessage: messages.chatUpdateMindMap,
		},
		{
			value: 'flowchart',
			label: messages.remixVariantFlowChart,
			updateChatMessage: messages.chatUpdateFlowChart,
		},
	],
};

/**
 * Build a subtype-to-backend-type map.  Prefers `emitType` when set so
 * that the backend receives the correct type even when a subtype is displayed
 * under a different UI category.
 */
function buildEmitTypeBySubtype(
	variantMap: Record<string, VariantOption[]>,
): Record<string, string> {
	return Object.entries(variantMap).reduce<Record<string, string>>((acc, [remixType, variants]) => {
		for (const variant of variants) {
			acc[variant.value] = variant.emitType ?? remixType;
		}
		return acc;
	}, {});
}

/**
 * Reverse mapping from subtype to remix type.
 * Each subtype is unique to one remix type, so we can infer the type from the subtype.
 */
function buildRemixTypeBySubtype(
	variantMap: Record<string, VariantOption[]>,
): Record<string, string> {
	return Object.entries(variantMap).reduce<Record<string, string>>((acc, [remixType, variants]) => {
		for (const variant of variants) {
			acc[variant.value] = remixType;
		}
		return acc;
	}, {});
}

/**
 * Reports whether an update was refused or retained by the lifecycle owner.
 *
 * The token is attempt provenance, not a display or update-dedup id. It lets a producer distinguish
 * the lifecycle it just admitted from an already-running lifecycle owned by another entry point.
 */
export type UpdateRemix = () =>
	| 'active-flow-rejected'
	| {
			status: 'retained';
			lifecycle: Pick<RetainedEditorRemixLifecycle, 'remixId' | 'token'>;
	  }
	| undefined;

export type UseUpdateRemixReturn = {
	updateRemix: UpdateRemix;
};

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
	typeof (value as PromiseLike<unknown> | null)?.then === 'function';

const resolveLifecycleContextResolution = (targetNodeId?: string, updateSentinel?: string) => {
	if (!targetNodeId) {
		return updateSentinel ? 'missingTargetNodeId' : 'missingTargetNodeIdAndUpdateSentinel';
	}

	return updateSentinel ? 'ready' : 'missingUpdateSentinel';
};

export function useUpdateRemix(): UseUpdateRemixReturn {
	const { formatMessage } = useIntl();
	const lifecycleOwner = useEditorRemixLifecycleOwner();
	const { sendMessage } = useSmartCreationChatStoreApi();
	const experienceTracker = useAIMateExperienceTracker();
	const { preferences } = useUserPreferences();
	const {
		isUpdate,
		dynamicUiSubtypeForUpdate,
		historyEntryForUpdate,
		setFinalMessage,
		setConversationId,
		setContentClassification,
		remixUpdateSentinelRef,
		isViewMode,
		editorRemixLifecycleAdapter,
	} = useEditorRemixContext();
	const { getMessages } = useChatMessagesActions();

	const variantsByRemixType: Record<string, VariantOption[]> = useMemo(
		() => ({
			...staticVariantsByRemixType,
			infographic: getInfographicVariants({
				useNewStyles: true,
			}),
			'visual-aid': getVisualAidVariants({
				enableImage: true,
			}),
		}),
		[],
	);
	const remixTypeBySubtype = useMemo(
		() => buildRemixTypeBySubtype(variantsByRemixType),
		[variantsByRemixType],
	);
	const emitTypeBySubtype = useMemo(
		() => buildEmitTypeBySubtype(variantsByRemixType),
		[variantsByRemixType],
	);

	const updateRemix: UpdateRemix = useCallback(() => {
		if (!fg('platform_editor_ai_remix_toolbar')) {
			return;
		}
		if (isViewMode || !isUpdate || !historyEntryForUpdate || !dynamicUiSubtypeForUpdate) {
			return;
		}

		const { conversationId, dynamicUiSource, dynamicUiSubtype, dynamicUiType, finalMessage } =
			historyEntryForUpdate;

		// Read the conversation once at the update boundary. The UI dedup sentinel is the final
		// message id currently displayed, while lifecycle admission must compare the result action's
		// invocation id (whiteboard updates intentionally reuse the same contentId).
		const messages = getMessages(conversationId);
		const uiFinalMessageSentinel = getFinalMessageId(getFinalAIFCMessage(messages));
		// The live lookup is bounded by the latest HUMAN turn. After a cancelled
		// update that turn has no result, fall back only to the baseline stored for
		// the selected displayed artifact.
		const liveLifecycleInvocationSentinel = getRemixResultPayload(messages)?.invocationId;
		const lifecycleInvocationSentinel =
			liveLifecycleInvocationSentinel ?? historyEntryForUpdate.admissionBaselineInvocationId;
		const sentinelSource = liveLifecycleInvocationSentinel
			? ('live-current-turn' as const)
			: historyEntryForUpdate.admissionBaselineInvocationId
				? ('artifact-history' as const)
				: ('none' as const);

		const remixType =
			(dynamicUiSubtype && remixTypeBySubtype[dynamicUiSubtype]) ?? dynamicUiType ?? 'custom';
		const remixVariants = (variantsByRemixType[remixType] ?? []).map((v) => {
			const formattedLabel = formatMessage(v.label);
			const formattedChatUpdateMessage = formatMessage(v.updateChatMessage);
			return {
				value: v.value,
				label: formattedLabel,
				chatUpdateMessage: formattedChatUpdateMessage,
			};
		});
		const selectedVariant =
			remixVariants.find((v) => v.value === dynamicUiSubtypeForUpdate) ?? remixVariants[0];

		// Only extract whiteboardContentId for whiteboard actions — chart actions
		// include contentId that would otherwise wrongly trigger shouldOverrideContentId.
		const currentDisplayActionKey = finalMessage?.content?.message?.message?.actions?.[0]?.key;
		const isWhiteboardAction =
			currentDisplayActionKey === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
			currentDisplayActionKey === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY;

		// Start remixRegenerate experience — succeed/fail are settled by the staging-area preview components.
		const currentMediaFileId = getMediaIdFromMessage(finalMessage);
		const contentMauiId =
			getMauiIdFromUrl(historyEntryForUpdate.url) ??
			getRemixIdFromMessage({
				message: finalMessage,
				actionKey: DYNAMIC_UI_CREATE_KEY,
			});
		// CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY has no case in getRemixIdFromPreview, use CREATE key for both.
		const whiteboardContentId = isWhiteboardAction
			? getRemixIdFromMessage({
					message: finalMessage,
					actionKey: CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
				})
			: undefined;
		const targetNodeId = contentMauiId ?? whiteboardContentId ?? currentMediaFileId;
		const artifactType = isWhiteboardAction
			? ('whiteboard' as const)
			: currentMediaFileId || dynamicUiType === 'image'
				? ('image' as const)
				: ('maui' as const);
		const lifecycleContextResolution = resolveLifecycleContextResolution(
			targetNodeId,
			lifecycleInvocationSentinel,
		);

		let retainedLifecycle: RetainedEditorRemixLifecycle | undefined;
		let resolvedConversationId = conversationId;
		if (!editorRemixLifecycleAdapter || !targetNodeId || !lifecycleInvocationSentinel) {
			if (editorRemixLifecycleAdapter) {
				const missingContextReason = !targetNodeId
					? lifecycleInvocationSentinel
						? ('missing-target' as const)
						: ('missing-target-and-sentinel' as const)
					: ('missing-sentinel' as const);
				try {
					editorRemixLifecycleAdapter.observeLifecycle?.({
						actionSubjectId: 'admission',
						producer: 'variant-update',
						artifactType,
						sentinelSource,
						outcome: lifecycleOwner?.active ? 'rejected' : 'legacy',
						reason: lifecycleOwner?.active ? 'active-flow' : missingContextReason,
					});
				} catch {
					// Ownership observations must never affect legacy update behavior.
				}
			}
			if (lifecycleOwner?.active) {
				// Fail closed the same way admission would have. Missing context is not a licence to run
				// a second flow: the live one still owns its terminal effects, so proceeding would both
				// run two flows at once and unfence the legacy effects the live flow owns. No request
				// goes out, so the caller has to take its thinking UI back down.
				return 'active-flow-rejected';
			}
			// This update runs as a legacy flow, so a previous flow's machine authority must not outlive
			// it. `admitAndRetainEditorRemixLifecycle` is the only other place that clears the latch, and
			// it is skipped here, which would otherwise leave this update's legacy terminal effects fenced
			// for an owner that is not running them.
			lifecycleOwner?.forgetMachineAuthority();
		} else {
			const lifecycleRetention = admitAndRetainEditorRemixLifecycle({
				adapter: editorRemixLifecycleAdapter,
				owner: lifecycleOwner,
				request: {
					kind: 'update',
					conversationId,
					targetNodeId,
					updateSentinel: lifecycleInvocationSentinel,
					messageCountBeforeSend: messages?.length ?? 0,
					observation: {
						producer: 'variant-update',
						artifactType,
						sentinelSource,
					},
				},
			});
			if (lifecycleRetention.status === 'active-flow-rejected') {
				// No request goes out, so the caller has to take its thinking UI back down.
				return 'active-flow-rejected';
			}

			if (lifecycleRetention.status === 'retained') {
				retainedLifecycle = lifecycleRetention.lifecycle;
				resolvedConversationId = retainedLifecycle.conversationId;
			}
		}
		remixUpdateSentinelRef.current = uiFinalMessageSentinel;
		setFinalMessage(undefined);
		setConversationId(resolvedConversationId);
		if (fg('platform_maui_stream_on_subtype_change')) {
			setContentClassification(undefined);
		}

		const editExperienceId = targetNodeId ?? 'unknown';
		experienceTracker.start({
			name: ExperienceName.REMIX_REGENERATE,
			id: editExperienceId,
			attributes: {
				appId: contentMauiId,
				contentMauiId,
				mediaFileId: currentMediaFileId,
				whiteboardContentId,
				remixType: dynamicUiType,
				remixSubtype: dynamicUiSubtypeForUpdate ?? dynamicUiSubtype,
				hasSubtypeChange: dynamicUiSubtype !== (dynamicUiSubtypeForUpdate ?? dynamicUiSubtype),
				conversationChannelId: resolvedConversationId,
				editSource: 'typeSwitcher',
				...(editorRemixLifecycleAdapter ? { lifecycleContextResolution } : {}),
			},
		});

		// if there's a whiteboard contentId, we need to override the contentId to be
		// the existing whiteboard contentId to be able to update it.
		// we don't do this though if the subtype is set because for changing subtypes, we
		// want a new whiteboard creation instead of updating existing board
		const shouldOverrideContentId = whiteboardContentId && !dynamicUiSubtypeForUpdate;

		// Omit mediaFileId for infographic style updates (marked by subtype being defined)
		const mediaFileId =
			dynamicUiSubtypeForUpdate === undefined ? getMediaIdFromMessage(finalMessage) : undefined;

		const resolvedUiType = shouldOverrideContentId
			? undefined
			: dynamicUiSubtypeForUpdate
				? emitTypeBySubtype[dynamicUiSubtypeForUpdate]
				: dynamicUiType;
		const isCustom = resolvedUiType === 'custom';
		const experience = resolvedUiType
			? isCustom
				? ('remix_custom' as const)
				: ('remix_edit' as const)
			: undefined;
		const isRemixExperienceContextEnabled = expValEquals(
			'rovo_remix_experience_context',
			'isEnabled',
			true,
		);

		const prompt: DocNode = {
			type: 'doc',
			version: 1,
			content: [
				{
					type: 'paragraph',
					content: [{ type: 'text', text: selectedVariant?.chatUpdateMessage }],
				},
			],
		};

		const additionalAnalyticAttributes = dynamicUiSubtype
			? {
					remixVariantBefore: dynamicUiSubtype,
					remixVariantAfter: dynamicUiSubtypeForUpdate ?? dynamicUiSubtype,
					remixVariantChanged: dynamicUiSubtype !== (dynamicUiSubtypeForUpdate ?? dynamicUiSubtype),
				}
			: undefined;

		const rollbackLifecycle = () => {
			retainedLifecycle?.rollback();
		};

		const acceptsRetainedStreamCallback = () =>
			!retainedLifecycle || (retainedLifecycle.isCurrent?.() ?? true);

		try {
			const sendResult: unknown = sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: resolvedConversationId,
				messageSources: {
					interactionSource: 'editor',
					// Use the same source for follow-ups: Remix doesn't have a separate follow-up entry point, but the "viewed" AIMAU analytics event uses `followUpSource`.
					followUpSource: 'editor',
				},
				additionalAnalyticAttributes,
				productKey: 'confluence',
				contentContext: 'global',
				minionAlias: 'remix-content',
				skipEditorContext: true,
				promptStreamHook: fg('platform_maui_stream_on_subtype_change')
					? {
							onActionClassification: async (actionClassification: StreamActionClassification) => {
								if (!acceptsRetainedStreamCallback()) {
									return;
								}
								setContentClassification(
									getContentClassificationFromActionClassification(actionClassification),
								);
							},
							onActionPart: async (actionPart: StreamActionPart | StreamActionPrepare) => {
								if (!acceptsRetainedStreamCallback()) {
									return;
								}
								if (
									actionPart.type === 'ACTION_PART' &&
									actionPart.message.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY
								) {
									setContentClassification((classification) =>
										withWhiteboardCreateActionData(classification, actionPart.message.data),
									);
								}
							},
						}
					: undefined,
				creationContextParams: {
					source: 'remix' as const,
					...(isRemixExperienceContextEnabled && experience ? { experience } : {}),
					...(isRemixExperienceContextEnabled && !isCustom && resolvedUiType
						? { blocks: [resolvedUiType] }
						: {}),
					contentMauiId,
					dynamicUiType: resolvedUiType,
					dynamicUiSubtype: dynamicUiSubtypeForUpdate ?? dynamicUiSubtype,
					dynamicUiSource,
					// TODO check if this is correct
					mediaFileId,
					...(shouldOverrideContentId
						? { isViewMode: false }
						: isViewMode
							? { isViewMode: true }
							: {}),
				},
				contentId: shouldOverrideContentId ? whiteboardContentId : undefined,
			});

			if (retainedLifecycle && isThenable(sendResult)) {
				void Promise.resolve(sendResult).catch(rollbackLifecycle);
			}
		} catch (error) {
			rollbackLifecycle();
			throw error;
		}

		return retainedLifecycle
			? {
					status: 'retained',
					lifecycle: {
						remixId: retainedLifecycle.remixId,
						token: retainedLifecycle.token,
					},
				}
			: undefined;
	}, [
		setConversationId,
		setContentClassification,
		variantsByRemixType,
		isViewMode,
		formatMessage,
		emitTypeBySubtype,
		sendMessage,
		preferences.storeUserMessages,
		remixTypeBySubtype,
		experienceTracker,
		setFinalMessage,
		isUpdate,
		historyEntryForUpdate,
		dynamicUiSubtypeForUpdate,
		getMessages,
		remixUpdateSentinelRef,
		editorRemixLifecycleAdapter,
		lifecycleOwner,
	]);

	return {
		updateRemix,
	};
}
